// @ts-strict-ignore
import {apiClient} from 'common/util/api-client';
import {
  ProjectShareGrant,
  SharableEntityGrant,
  SharableEntityType,
  TeamShareGrant,
  UserShareGrant,
} from 'common/report/util/saved-entities/types';
import {isTestEnv} from 'common/report/util/env';

import {appApiUrl} from '../utils';
import {deleteFromFetchCache} from '../utils/fetch-cache';

interface SharableEntityRequest {
  projectId: number;
  entityId: number;
  entityType: SharableEntityType;
  ignoreCache?: boolean;
}

interface SharableEntityGrantUpdate {
  id: number;
  projectShares?: ProjectShareGrant[];
  userShares?: UserShareGrant[];
  teamShares?: TeamShareGrant[];
}

interface SharableEntityGrantDelete {
  id: number;
  /** Array of project IDs to remove (should only ever be at most 1) */
  projectShares?: number[];
  /** Array of user emails to remove */
  userShares?: string[];
  /** Array of team IDs to remove */
  teamShares?: number[];
}

interface RecentGrants {
  shared: SharableEntityGrant[];
  updated: SharableEntityGrant[];
}

class SharedEntitiesClient {
  async getGrants({
    projectId,
    entityId,
    entityType,
    ignoreCache = false,
  }: SharableEntityRequest): Promise<SharableEntityGrant> {
    const {cache, network} = apiClient.hydratedGet<SharableEntityGrant>(
      appApiUrl(`shared-entities/${entityType}/${entityId}`, {projectId}),
    );
    const cachedResult = await cache;
    if (cachedResult && !isTestEnv() && !ignoreCache) {
      return cachedResult;
    }
    return network;
  }

  updateGrants(
    {projectId, entityId, entityType}: SharableEntityRequest,
    {
      projectShares,
      userShares,
      teamShares,
    }: {projectShares?: ProjectShareGrant[]; userShares?: UserShareGrant[]; teamShares?: TeamShareGrant[]},
  ): Promise<SharableEntityGrant> {
    const payload: SharableEntityGrantUpdate = {id: entityId};
    if (projectShares) {
      payload.projectShares = projectShares;
    }
    if (userShares) {
      payload.userShares = userShares;
    }
    if (teamShares) {
      payload.teamShares = teamShares;
    }

    // Delete existing value from fetchCache because we are about to modify the object
    deleteFromFetchCache(appApiUrl(`shared-entities/${entityType}/${entityId}`, {projectId}), {
      method: `GET`,
    });

    return apiClient.post(appApiUrl(`shared-entities/${entityType}/${entityId}/upsert`, {projectId}), payload);
  }

  removeGrants(
    {projectId, entityId, entityType}: SharableEntityRequest,
    {projectShares, userShares, teamShares}: {projectShares?: number[]; userShares?: string[]; teamShares?: number[]},
  ): Promise<SharableEntityGrant> {
    const payload: SharableEntityGrantDelete = {id: entityId};
    if (projectShares) {
      payload.projectShares = projectShares;
    }
    if (userShares) {
      payload.userShares = userShares;
    }
    if (teamShares) {
      payload.teamShares = teamShares;
    }

    // Delete existing value from fetchCache because we are about to modify the object
    deleteFromFetchCache(appApiUrl(`shared-entities/${entityType}/${entityId}`, {projectId}), {
      method: `GET`,
    });

    return apiClient.post(appApiUrl(`shared-entities/${entityType}/${entityId}/delete`, {projectId}), payload);
  }

  async recent({projectId, entityType}: {projectId: number; entityType: SharableEntityType}): Promise<RecentGrants> {
    const {cache, network} = apiClient.hydratedGet<RecentGrants>(
      appApiUrl(`shared-entities/${entityType}/recent`, {projectId}),
    );
    const cachedResult = await cache;
    if (cachedResult && !isTestEnv()) {
      return cachedResult;
    }
    return network;
  }
}

export const sharedEntitiesClient = new SharedEntitiesClient();
